import { create } from 'zustand';

import { MAX_TRADES } from './block-trade-context';

/** Last-known copy of the visible trade tape (newest first). */
export interface TapeSnapshot {
  /** Up to `MAX_TRADES` trade rows, opaque to the host. */
  readonly trades: readonly unknown[];
  /** Untruncated visible total at capture time. */
  readonly visibleCount: number;
  /** Capture time (ms epoch) so consumers can label staleness. */
  readonly capturedAt: number;
}

interface TapeSnapshotStore {
  readonly snapshot: TapeSnapshot | null;
  readonly setSnapshot: (
    trades: readonly unknown[],
    visibleCount: number,
  ) => void;
}

/**
 * Mirror of the Trade Tape tab's visible rows, written by
 * `TapeSnapshotMirror` on every tape update and read at send time by the
 * `/analyze` context builder. The point of the copy: the tape tab and the
 * terminal tab are mutually exclusive in bottom-tab pin mode, so a live read
 * through a tape provider is empty exactly when the user is in the terminal
 * — the flow where `/analyze` needs the tape.
 *
 * The snapshot deliberately survives the tape tab unmounting: for agent
 * context, a slightly stale tape beats no tape. `capturedAt` lets the
 * context builder say how fresh it is.
 */
export const useTapeSnapshotStore = create<TapeSnapshotStore>((set) => ({
  snapshot: null,
  setSnapshot: (trades, visibleCount) => {
    set({
      snapshot: {
        trades: trades.slice(0, MAX_TRADES),
        visibleCount,
        capturedAt: Date.now(),
      },
    });
  },
}));
